import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoadPanel } from "devextreme-react";
import notify from "devextreme/ui/notify";
import { Ticket, getTickets, isClosedStatus } from "../../../api/support";

// Temsilci performans raporu: Ticketleri assigned_to'ya gore toplar (6500).
// Backend bos donerse ornek (mock) temsilci verisi gosterilir.
type AgentRow = {
    ref: string;
    name: string;
    assigned: number;
    resolved: number;
    open: number;
    avgResolutionDays: number | null;
};

const MOCK_AGENTS: AgentRow[] = [
    { ref: "m1", name: "Ahmed Enes Demiryürek", assigned: 64, resolved: 58, open: 6, avgResolutionDays: 1.4 },
    { ref: "m2", name: "Ayhan Dölek", assigned: 52, resolved: 45, open: 7, avgResolutionDays: 1.9 },
    { ref: "m3", name: "Rahime Kalkan", assigned: 47, resolved: 44, open: 3, avgResolutionDays: 1.1 },
    { ref: "m4", name: "Arif Uçar", assigned: 38, resolved: 30, open: 8, avgResolutionDays: 2.6 },
    { ref: "m5", name: "Şevket Çelikkaya", assigned: 29, resolved: 27, open: 2, avgResolutionDays: 0.9 },
];

export default function AgentPerformancePage() {
    const { t } = useTranslation();
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        getTickets()
            .then(setTickets)
            .catch(() => notify(t("ticketsLoadError"), "error", 2000))
            .finally(() => setLoading(false));
    }, [t]);

    const agents = useMemo<AgentRow[]>(() => {
        const map = new Map<string, AgentRow & { _sumDays: number; _closed: number }>();
        for (const tk of tickets) {
            const ref = tk.assigned_to || "";
            if (!ref) continue; // atanmamis Ticketler temsilci performansina girmez
            const name = tk.assigned_to_name || `#${ref}`;
            let row = map.get(ref);
            if (!row) {
                row = { ref, name, assigned: 0, resolved: 0, open: 0, avgResolutionDays: null, _sumDays: 0, _closed: 0 };
                map.set(ref, row);
            }
            row.assigned++;
            if (isClosedStatus(tk.status)) {
                row.resolved++;
                if (tk.closed_at && tk.created_at) {
                    const d = (new Date(tk.closed_at).getTime() - new Date(tk.created_at).getTime()) / 86400000;
                    if (d >= 0) { row._sumDays += d; row._closed++; }
                }
            } else {
                row.open++;
            }
        }
        const rows = Array.from(map.values()).map(r => ({
            ref: r.ref, name: r.name, assigned: r.assigned, resolved: r.resolved, open: r.open,
            avgResolutionDays: r._closed > 0 ? Number((r._sumDays / r._closed).toFixed(1)) : null,
        }));
        rows.sort((a, b) => b.resolved - a.resolved);
        return rows.length ? rows : MOCK_AGENTS;
    }, [tickets]);

    const totals = useMemo(() => {
        const totalAssigned = agents.reduce((s, a) => s + a.assigned, 0);
        const totalResolved = agents.reduce((s, a) => s + a.resolved, 0);
        const resolutionRate = totalAssigned > 0 ? Math.round((totalResolved / totalAssigned) * 100) : 0;
        const withAvg = agents.filter(a => a.avgResolutionDays != null);
        const avgRes = withAvg.length ? (withAvg.reduce((s, a) => s + (a.avgResolutionDays as number), 0) / withAvg.length).toFixed(1) : null;
        const top = agents.reduce<AgentRow | null>((best, a) => (!best || a.resolved > best.resolved ? a : best), null);
        return { totalAgents: agents.length, totalAssigned, totalResolved, resolutionRate, avgRes, top };
    }, [agents]);

    const maxAssigned = Math.max(1, ...agents.map(a => a.assigned));

    return (
        <div className="h-full w-full" style={{ overflowY: "auto", padding: 20, color: "var(--base-text-color)" }}>
            <LoadPanel visible={loading} />

            <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>{t("agentPerformanceTitle")}</h2>

            {/* Ozet kartlar */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" style={{ marginBottom: 20 }}>
                <Metric label={t("totalAgents")} value={totals.totalAgents} color="#6366F1" />
                <Metric label={t("ticketsHandled")} value={totals.totalAssigned} color="#0EA5E9" />
                <Metric label={t("resolved")} value={totals.totalResolved} color="#10B981" />
                <Metric label={t("resolutionRate")} value={`%${totals.resolutionRate}`} color="#F97316" />
                <Metric label={t("avgResolutionTime")} value={totals.avgRes ? `${totals.avgRes} ${t("daysShort")}` : "—"} color="#8B5CF6" />
            </div>

            {totals.top && (
                <div className="text-xs" style={{ marginBottom: 16, color: "var(--subtitle-text-color)" }}>
                    🏆 {t("topPerformer")}: <strong style={{ color: "var(--base-text-color)" }}>{totals.top.name}</strong> ({totals.top.resolved} {t("resolved").toLowerCase()})
                </div>
            )}

            {/* Temsilci tablosu */}
            <div className="text-sm font-semibold uppercase" style={{ letterSpacing: "0.04em", color: "var(--subtitle-text-color)", marginBottom: 8 }}>
                {t("agentPerformance")}
            </div>
            <div style={{ background: "var(--base-bg)", border: "1px solid var(--border-color)", borderRadius: 10, overflow: "hidden" }}>
                {agents.length === 0 ? (
                    <div className="text-sm" style={{ padding: 16, color: "var(--subtitle-text-color)" }}>{t("noAgentData")}</div>
                ) : (
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                            <tr style={{ textAlign: "left", color: "var(--subtitle-text-color)", fontSize: 12 }}>
                                <th style={thStyle}>{t("agent")}</th>
                                <th style={thStyle}>{t("ticketsAssigned")}</th>
                                <th style={thStyle}>{t("resolved")}</th>
                                <th style={thStyle}>{t("open")}</th>
                                <th style={thStyle}>{t("resolutionRate")}</th>
                                <th style={thStyle}>{t("avgResolutionTime")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {agents.map(a => {
                                const rate = a.assigned > 0 ? Math.round((a.resolved / a.assigned) * 100) : 0;
                                const initials = a.name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
                                return (
                                    <tr key={a.ref} style={{ borderTop: "1px solid var(--border-color)" }}>
                                        <td style={tdStyle}>
                                            <div className="flex items-center gap-2">
                                                <span className="flex items-center justify-center shrink-0" style={{ width: 28, height: 28, borderRadius: 7, background: "var(--accent-color, #6366F1)", color: "#fff", fontSize: 11, fontWeight: 600 }}>{initials}</span>
                                                <span className="text-sm">{a.name}</span>
                                            </div>
                                        </td>
                                        <td style={tdStyle}>
                                            <div className="flex items-center gap-2">
                                                <div style={{ width: 70, height: 6, borderRadius: 999, background: "var(--base-bg-darken-4)", overflow: "hidden" }}>
                                                    <div style={{ width: `${(a.assigned / maxAssigned) * 100}%`, height: "100%", background: "#0EA5E9" }} />
                                                </div>
                                                <span className="text-sm">{a.assigned}</span>
                                            </div>
                                        </td>
                                        <td style={tdStyle}><span className="text-sm" style={{ color: "#10B981", fontWeight: 600 }}>{a.resolved}</span></td>
                                        <td style={tdStyle}><span className="text-sm" style={{ color: a.open > 0 ? "#F97316" : "var(--subtitle-text-color)" }}>{a.open}</span></td>
                                        <td style={tdStyle}>
                                            <span style={{ fontWeight: 600, color: rate >= 80 ? "#10B981" : rate >= 50 ? "#F97316" : "#EF4444" }}>%{rate}</span>
                                        </td>
                                        <td style={tdStyle}><span className="text-sm">{a.avgResolutionDays != null ? `${a.avgResolutionDays} ${t("daysShort")}` : "—"}</span></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

const thStyle: React.CSSProperties = { padding: "10px 14px", fontWeight: 600 };
const tdStyle: React.CSSProperties = { padding: "10px 14px", verticalAlign: "middle" };

function Metric({ label, value, color }: { label: string; value: string | number; color: string }) {
    return (
        <div style={{ background: "var(--base-bg)", border: "1px solid var(--border-color)", borderLeft: `4px solid ${color}`, borderRadius: 12, padding: "14px 16px" }}>
            <div style={{ fontSize: 28, fontWeight: 700, color }}>{value}</div>
            <div className="text-xs" style={{ color: "var(--subtitle-text-color)", marginTop: 2 }}>{label}</div>
        </div>
    );
}
